
import { __ } from 'bits/intl'
import { Component, Getter, namespace, Vue } from 'bits/vue'
import { getWallAccessPrivacyTypeName, getWallAccessPrivacyTypeText } from 'bits/wall_access_settings_helper'
import { LibraryType, WallAccessPrivacyType, WallAccessRight } from 'enums'
import BaseIcon from 'vuecomponents/base_icon.vue'
import { ShareSubpanel } from 'vuexstore/modules/share'

const PERMISSION_TEXT: { [key: string]: string } = {
  [WallAccessRight.Read]: __('Visitors can <strong>read</strong>.'),
  [WallAccessRight.Write]: __('Visitors can <strong>write</strong>.'),
  [WallAccessRight.Edit]: __('Visitors can <strong>edit</strong>.'),
  [WallAccessRight.Administer]: __('Visitors can <strong>administer</strong>.'),
}

const LOGGED_IN_PERMISSION_TEXT: { [key: string]: string } = {
  [WallAccessRight.Read]: __('Logged in visitors can <strong>read</strong>.'),
  [WallAccessRight.Write]: __('Logged in visitors can <strong>write</strong>.'),
  [WallAccessRight.Edit]: __('Logged in visitors can <strong>edit</strong>.'),
  [WallAccessRight.Administer]: __('Logged in visitors can <strong>administer</strong>.'),
}

const share = namespace('share')
const wallAccessSettings = namespace('wallAccessSettings')

@Component({
  components: {
    BaseIcon,
  },
})
class PrivacySection extends Vue {
  @Getter organizationName!: string
  @Getter libraryType!: LibraryType
  @Getter isSchoolLibrary!: boolean
  @Getter isTeamLibrary!: boolean
  @Getter isClassroomLibrary!: boolean

  @wallAccessSettings.Getter password!: string | null
  @wallAccessSettings.Getter privacyType!: WallAccessPrivacyType
  @wallAccessSettings.Getter right!: WallAccessRight

  @share.Action changeActivePanel

  get xPassword() {
    return this.privacyType === WallAccessPrivacyType.PasswordProtected
  }

  get xPermission() {
    return this.privacyType !== WallAccessPrivacyType.Private
  }

  get xDisplayedInLibraryDashboardText() {
    return this.privacyType === WallAccessPrivacyType.LibraryWideListed
  }

  get displayedInLibraryDashboardText() {
    if (this.isSchoolLibrary) return __('Displayed in school dashboard.')
    if (this.isClassroomLibrary) return __('Displayed in classroom dashboard.')
    return __('Displayed in team dashboard.')
  }

  get xNotDisplayedInLibraryDashboardText() {
    return this.privacyType === WallAccessPrivacyType.LibraryWideUnlisted
  }

  get notDisplayedInLibraryDashboardText() {
    if (this.isSchoolLibrary) return __('Will not display in school dashboard.')
    if (this.isClassroomLibrary) return __('Will not display in classroom dashboard.')
    return __('Will not display in team dashboard.')
  }

  get xDisplayedInOrgDashboardText() {
    return this.privacyType === WallAccessPrivacyType.OrgWideListed
  }

  get xNotDisplayedInOrgDashboardText() {
    return this.privacyType === WallAccessPrivacyType.OrgWideUnlisted
  }

  get privacyTypeName() {
    return getWallAccessPrivacyTypeName({ libraryType: this.libraryType, length: 'long' })[this.privacyType]
  }

  get privacyText() {
    return getWallAccessPrivacyTypeText({ organization: this.organizationName })
  }

  get privacyTypeDescription() {
    return this.privacyText[this.privacyType]
  }

  get passwordText(): string {
    return __('The password is <strong>%{password}</strong>.', {
      password: this.password,
    })
  }

  get permissionText(): string {
    if (this.privacyType === WallAccessPrivacyType.LoggedInUsersOnly) {
      return LOGGED_IN_PERMISSION_TEXT[this.right]
    }
    return PERMISSION_TEXT[this.right]
  }

  openPrivacyPanel(e): void {
    this.changeActivePanel(ShareSubpanel.Privacy)
  }

  openPrivacyModal(): void {
    this.$emit('open-privacy-modal')
  }
}

export default PrivacySection
